/**
 * First-party cookie consent, shared in shape with the banner on bonded.studio
 * (wp-content/plugins/bonded-consent) so the same receipt means the same thing
 * on every Bonded property.
 *
 * Nothing in this app currently sets a cookie or loads a third-party tag, so
 * today the banner grants a capability that is not yet wired up. It exists so
 * that when analytics does arrive it is gated from the first commit rather than
 * retrofitted — the failure mode we already hit once on bonded.studio, where a
 * tag loaded unconditionally for four days because the gate had gone missing.
 *
 * Kept byte-identical to the copies in the other web apps. There is no shared
 * workspace package for them yet; if one is added, this is the first thing that
 * should move into it.
 *
 * Storage is a cookie scoped to `.bonded.studio` so one answer covers the help
 * centre, the web app and the funnel. On any other host (localhost, previews)
 * it falls back to localStorage.
 */

export const CONSENT_COOKIE = 'bonded_cookie_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_SECONDS = 15552000; // 180 days, matching bonded.studio
export const CONSENT_EVENT = 'bonded:consent-change';

export type ConsentReceipt = {
  /** Schema version. A bump re-asks everyone. */
  v: number;
  /** Stable id so a visitor can be answered if they ask what they consented to. */
  id: string;
  analytics: boolean;
  decidedAt: string;
};

function isBondedHost(): boolean {
  return typeof location !== 'undefined' && /(^|\.)bonded\.studio$/.test(location.hostname);
}

function uuid(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((n) => (n + 0x100).toString(16).slice(1)).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function readRaw(): string | null {
  try {
    if (isBondedHost()) {
      const prefix = `${CONSENT_COOKIE}=`;
      const hit = document.cookie.split(';').map((p) => p.trim()).find((p) => p.startsWith(prefix));
      if (hit) return decodeURIComponent(hit.slice(prefix.length));
      return null;
    }
    return localStorage.getItem(CONSENT_COOKIE);
  } catch {
    // Private mode, blocked storage: treat as "no decision yet" rather than throwing.
    return null;
  }
}

function writeRaw(value: string): void {
  try {
    if (isBondedHost()) {
      let cookie = `${CONSENT_COOKIE}=${encodeURIComponent(value)}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax; Domain=.bonded.studio`;
      if (location.protocol === 'https:') cookie += '; Secure';
      document.cookie = cookie;
      return;
    }
    localStorage.setItem(CONSENT_COOKIE, value);
  } catch {
    // A visitor who blocks storage simply gets asked again next visit.
  }
}

export function readConsent(): ConsentReceipt | null {
  const raw = readRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentReceipt>;
    const decidedAt = Date.parse(String(parsed.decidedAt));
    if (
      parsed.v !== CONSENT_VERSION ||
      typeof parsed.analytics !== 'boolean' ||
      !Number.isFinite(decidedAt) ||
      Date.now() >= decidedAt + CONSENT_MAX_AGE_SECONDS * 1000
    ) {
      return null;
    }
    return { v: CONSENT_VERSION, id: String(parsed.id ?? uuid()), analytics: parsed.analytics, decidedAt: String(parsed.decidedAt) };
  } catch {
    return null;
  }
}

export function writeConsent(analytics: boolean): ConsentReceipt {
  const existing = readConsent();
  const receipt: ConsentReceipt = {
    v: CONSENT_VERSION,
    id: existing?.id ?? uuid(),
    analytics,
    decidedAt: new Date().toISOString(),
  };
  writeRaw(JSON.stringify(receipt));
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: receipt }));
  } catch {
    /* no-op */
  }
  return receipt;
}

/**
 * The gate every future analytics call must pass through. Denied by default:
 * no decision means no consent, never the other way round.
 */
export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}
